import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getWordStartPositions } from "../../admin/pages/themeupdate/crosswordLayout";
import { getCrossword, getCrosswordWords } from "./stageContent";
import { StageLoader, StageShell, useStageRun } from "./stageShared";
import { nyAlert, showStageResult } from "../nyPopups";

/** Stage 2 — crossword (Admin → Game Content → Down and Across). */
const STAGE_NUM = 2;
const DEFAULT_HEADING = "Down and Across";

const cellKey = (r, c) => `${r}-${c}`;

/* Fill colors for numbered word-start cells, cycled by clue number (as in down_across). */
const START_CELL_COLORS = ["#ef3b5d", "#9333ea", "#2f6fed", "#f59e0b", "#14b8a6", "#ec4899", "#22c55e", "#8b5cf6"];

/** Delay between neighbouring cells' flips when a word is solved (domino effect). */
const FLIP_STEP_MS = 90;
const FLIP_MS = 700;

export default function Stage2Crossword() {
  const run = useStageRun(STAGE_NUM);
  const { themeData, isLoading, pointsPerCorrect, addPoints, finishStage, startTimer } = run;

  const heading = String(themeData?.downandacross_title || "").trim() || DEFAULT_HEADING;
  const subheading = String(themeData?.downandacross_subtitle || "").trim();

  const layout = useMemo(() => getCrossword(themeData), [themeData]);
  const words = useMemo(() => getCrosswordWords(layout), [layout]);
  const numbers = useMemo(() => getWordStartPositions(words), [words]);

  // cell → indexes of the words passing through it
  const wordsByCell = useMemo(() => {
    const map = new Map();
    words.forEach((w) =>
      w.cells.forEach(([r, c]) => {
        const k = cellKey(r, c);
        map.set(k, [...(map.get(k) || []), w.index]);
      })
    );
    return map;
  }, [words]);

  // cell → the right letter (upper case); a cell shared by two words has one letter.
  const solution = useMemo(() => {
    const map = {};
    words.forEach((w) =>
      w.cells.forEach(([r, c], k) => {
        map[cellKey(r, c)] = w.answer[k].toUpperCase();
      })
    );
    return map;
  }, [words]);

  const [entries, setEntries] = useState({});
  const [active, setActive] = useState({ cell: null, word: null });
  const [result, setResult] = useState(null); // { [cellKey]: true/false } once submitted
  const [busy, setBusy] = useState(false);
  const [flipping, setFlipping] = useState({}); // { [cellKey]: delay ms } while a solved word flips
  const inputRefs = useRef({});
  const finished = useRef(false);

  useEffect(() => {
    if (isLoading || finished.current) return;
    if (!words.length) {
      finished.current = true;
      finishStage();
      return;
    }
    startTimer();
  }, [isLoading, words.length, finishStage, startTimer]);

  const focusCell = useCallback((key) => {
    const el = inputRefs.current[key];
    if (el) {
      el.focus();
      el.select?.();
    }
  }, []);

  const selectWord = useCallback(
    (wordIndex) => {
      const word = words[wordIndex];
      if (!word) return;
      const target = cellKey(...word.cells[0]);
      setActive({ cell: target, word: wordIndex });
      focusCell(target);
    },
    [words, focusCell]
  );

  /**
   * Which word a newly focused cell should activate (as in down_across): a cell that
   * starts exactly one word selects that word, whatever was active before. Only when it
   * starts both or neither (mid-word crossing) do we keep the word being typed.
   */
  const handleCellFocus = (key) => {
    const list = wordsByCell.get(key) || [];
    const starting = list.filter((i) => cellKey(...words[i].cells[0]) === key);
    setActive((prev) => {
      if (prev.cell === key) return prev;
      if (starting.length === 1) return { cell: key, word: starting[0] };
      const keepWord = list.includes(prev.word) ? prev.word : list[0];
      return { cell: key, word: keepWord };
    });
  };

  // Was the cell already the active one when the press began? (focus fires on mousedown,
  // so by click time `active` already points at it — this keeps the first click from toggling.)
  const wasActiveOnPress = useRef(false);

  // Clicking the active cell again switches between its across / down word.
  const handleCellClick = (key) => {
    const list = wordsByCell.get(key) || [];
    if (wasActiveOnPress.current && active.cell === key && list.length > 1) {
      const next = list[(list.indexOf(active.word) + 1) % list.length];
      setActive({ cell: key, word: next });
    }
  };

  const isLocked = (key, snapshot = entries) => Boolean(snapshot[key]) && snapshot[key] === solution[key];

  /** Move to the next (step 1) / previous (step -1) square of the active word that isn't locked. */
  const moveWithinWord = (key, step, snapshot = entries) => {
    const word = words[active.word];
    if (!word) return;
    let pos = word.cells.findIndex(([r, c]) => cellKey(r, c) === key) + step;
    while (pos >= 0 && pos < word.cells.length) {
      const nextKey = cellKey(...word.cells[pos]);
      if (!isLocked(nextKey, snapshot)) {
        setActive({ cell: nextKey, word: active.word });
        focusCell(nextKey);
        return;
      }
      pos += step;
    }
  };

  // Each letter is checked as it's typed: right → green and locked, wrong → red (retype it).
  const handleChange = (key, value) => {
    if (result || isLocked(key)) return;
    const letter = value.replace(/\s/g, "").slice(-1).toUpperCase();
    const next = { ...entries, [key]: letter };
    setEntries(next);
    if (letter === solution[key]) flipSolvedWords(key, next);
    if (letter) moveWithinWord(key, 1, next);
  };

  /** Domino flip across any word through `key` that this letter just completed. */
  const flipSolvedWords = (key, snapshot) => {
    const delays = {};
    (wordsByCell.get(key) || []).forEach((i) => {
      const cells = words[i].cells.map(([r, c]) => cellKey(r, c));
      if (!cells.every((k) => isLocked(k, snapshot))) return;
      cells.forEach((k, n) => {
        delays[k] = Math.min(delays[k] ?? Infinity, n * FLIP_STEP_MS);
      });
    });
    const keys = Object.keys(delays);
    if (!keys.length) return;
    setFlipping((prev) => ({ ...prev, ...delays }));
    setTimeout(() => {
      setFlipping((prev) => {
        const rest = { ...prev };
        keys.forEach((k) => delete rest[k]);
        return rest;
      });
    }, Math.max(...Object.values(delays)) + FLIP_MS);
  };

  const handleKeyDown = (key, e) => {
    if (e.key === "Backspace" && (!entries[key] || isLocked(key))) {
      e.preventDefault();
      moveWithinWord(key, -1);
      return;
    }
    const [r, c] = key.split("-").map(Number);
    const deltas = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    const d = deltas[e.key];
    if (!d) return;
    e.preventDefault();
    const nextKey = cellKey(r + d[0], c + d[1]);
    if (wordsByCell.has(nextKey)) focusCell(nextKey);
  };

  const finishCrossword = useCallback(async () => {
    if (busy || result || finished.current) return;
    setBusy(true);
    const cellResult = {};
    let correctWords = 0;
    const answers = words.map((w) => {
      const typed = w.cells.map(([r, c]) => entries[cellKey(r, c)] || "").join("");
      const isCorrect = typed.toLowerCase() === w.answer;
      if (isCorrect) correctWords += 1;
      w.cells.forEach(([r, c], k) => {
        const key = cellKey(r, c);
        cellResult[key] = (entries[key] || "").toLowerCase() === w.answer[k];
      });
      return {
        questionText: w.clue,
        userAnswer: typed,
        correctAnswer: w.answer.toUpperCase(),
        isCorrect,
        orientation: w.orientation,
      };
    });

    const points = correctWords * pointsPerCorrect;
    addPoints(points);
    setResult(cellResult);

    await showStageResult({ correct: correctWords, total: words.length, points });

    finished.current = true;
    finishStage({ answers });
  }, [busy, result, words, entries, pointsPerCorrect, addPoints, finishStage]);

  // Every square must hold a letter (right or wrong) before the crossword can be submitted.
  const handleSubmit = async () => {
    if (busy || result || finished.current) return;
    // Empty squares in reading order (top-to-bottom, left-to-right).
    const empty = [...wordsByCell.keys()]
      .filter((k) => !entries[k])
      .sort((a, b) => {
        const [ar, ac] = a.split("-").map(Number);
        const [br, bc] = b.split("-").map(Number);
        return ar - br || ac - bc;
      });
    if (empty.length > 0) {
      const count = empty.length;
      await nyAlert({
        kind: "info",
        title: "Fill every square",
        text: `${count} square${count === 1 ? " is" : "s are"} still empty. Fill them all to submit.`,
        confirmText: "Got it",
      });
      // Jump to the first empty square, in its word.
      const first = empty[0];
      const word = (wordsByCell.get(first) || [])[0];
      setActive({ cell: first, word });
      focusCell(first);
      return;
    }
    finishCrossword();
  };

  // Every square right → finish automatically.
  const allSolved =
    words.length > 0 && [...wordsByCell.keys()].every((k) => entries[k] && entries[k] === solution[k]);
  useEffect(() => {
    if (allSolved) finishCrossword();
  }, [allSolved, finishCrossword]);

  if (isLoading || !words.length) return <StageLoader />;

  const activeWord = words[active.word];
  const activeCells = new Set((activeWord?.cells || []).map(([r, c]) => cellKey(r, c)));
  const across = words.filter((w) => w.orientation === "across");
  const down = words.filter((w) => w.orientation === "down");
  // Same numbering as the grid cells (and the admin preview).
  const numberOf = (w) => numbers[`${w.starty},${w.startx}`];

  const renderClues = (title, list) => (
    <div className="cw-clue-group">
      <h2 className="cw-clue-title">{title}</h2>
      <ol className="cw-clue-list">
        {list.map((w) => (
          <li key={w.index}>
            <button
              type="button"
              className={`cw-clue${active.word === w.index ? " is-active" : ""}`}
              onClick={() => selectWord(w.index)}
            >
              <span className="cw-clue-num">{numberOf(w)}.</span>
              <span>{w.clue}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <StageShell run={run} heading={heading} subheading={subheading}>
      <main className="ny-content cw-stage">
        <div className="cw-board-wrap">
          <div
            className="cw-board"
            style={{
              "--cw-cols": layout.cols,
              "--cw-rows": layout.rows,
              gridTemplateColumns: `repeat(${layout.cols}, var(--cw-cell))`,
            }}
          >
            {Array.from({ length: layout.rows }, (_, r) =>
              Array.from({ length: layout.cols }, (_, c) => {
                const key = cellKey(r, c);
                if (!wordsByCell.has(key)) {
                  return <span key={key} className="cw-cell cw-cell--block" aria-hidden />;
                }
                const num = numbers[`${r + 1},${c + 1}`];
                const typed = entries[key] || "";
                const locked = Boolean(typed) && typed === solution[key];
                let state = "";
                if (result) state = result[key] ? " is-correct" : " is-wrong";
                else if (typed) state = locked ? " is-correct is-locked" : " is-wrong";
                else if (activeCells.has(key)) state = " is-in-word";
                if (!result && active.cell === key) state += " is-current";
                if (num) state += " cw-cell--start";
                const flipDelay = flipping[key];
                if (flipDelay !== undefined) state += " is-flipping";
                const cellStyle = {};
                if (num) cellStyle["--cw-start-color"] = START_CELL_COLORS[(num - 1) % START_CELL_COLORS.length];
                if (flipDelay !== undefined) cellStyle.animationDelay = `${flipDelay}ms`;
                return (
                  <span key={key} className={`cw-cell${state}`} style={cellStyle}>
                    {num ? <span className="cw-cell-num">{num}</span> : null}
                    {/* The visible letter is plain text centred in the square; the input on
                        top is invisible and only captures typing (inputs mis-centre text on
                        phones and make iOS zoom when their font is under 16px). */}
                    <span className="cw-cell-letter" aria-hidden>
                      {typed}
                    </span>
                    <input
                      ref={(el) => {
                        inputRefs.current[key] = el;
                      }}
                      className="cw-cell-input"
                      value={entries[key] || ""}
                      maxLength={2}
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      aria-label={`Row ${r + 1}, column ${c + 1}`}
                      onFocus={() => handleCellFocus(key)}
                      onMouseDown={() => {
                        wasActiveOnPress.current = active.cell === key;
                      }}
                      onClick={() => handleCellClick(key)}
                      onChange={(e) => handleChange(key, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(key, e)}
                      readOnly={Boolean(result) || locked}
                      aria-invalid={Boolean(typed) && !locked}
                    />
                  </span>
                );
              })
            )}
          </div>
        </div>

        <aside className="cw-clues ny-cream-card">
          <div className="cw-clue-scroll">
            {across.length ? renderClues("Across", across) : null}
            {down.length ? renderClues("Down", down) : null}
          </div>
          <button type="button" className="ny-btn ny-btn--primary cw-submit" onClick={handleSubmit} disabled={busy}>
            Submit
          </button>
        </aside>
      </main>
    </StageShell>
  );
}
